import { useState, useEffect } from "react";
import { Character } from "@/mocks/characters";
import { supabase, rowToCharacter, characterToRow, CharacterRow } from "@/lib/supabase";
import AdminLogin from "@/pages/admin/components/AdminLogin";
import AdminDashboard from "@/pages/admin/components/AdminDashboard";

const AUTH_KEY = "charamap_admin_auth";
const ADMIN_ID = "mappuoou";
const ADMIN_PW = "h98ya948y9q";

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCharacters = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("characters")
      .select("*")
      .order("sort_order", { ascending: true });
    if (!error && data) {
      setCharacters((data as CharacterRow[]).map(rowToCharacter));
    }
    setLoading(false);
  };

  useEffect(() => {
    const auth = sessionStorage.getItem(AUTH_KEY);
    if (auth === "true") setIsLoggedIn(true);
    fetchCharacters();
  }, []);

  const handleLogin = (id: string, pw: string): boolean => {
    if (id === ADMIN_ID && pw === ADMIN_PW) {
      sessionStorage.setItem(AUTH_KEY, "true");
      setIsLoggedIn(true);
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setIsLoggedIn(false);
  };

  const handleSave = async (chars: Character[], reorderOnly = false) => {
    if (reorderOnly) {
      // Sort order only — fast bulk upsert
      const updates = chars.map((c, i) => ({
        id: c.id,
        sort_order: i + 1,
        updated_at: new Date().toISOString(),
      }));
      await supabase.from("characters").upsert(updates, { onConflict: "id" });
      setCharacters(chars);
      return;
    }

    // Diff: find added, updated, deleted
    const existingIds = new Set(characters.map((c) => c.id));
    const newIds = new Set(chars.map((c) => c.id));

    // Deleted
    for (const c of characters) {
      if (!newIds.has(c.id)) {
        await supabase.from("characters").delete().eq("id", c.id);
      }
    }

    // Added or updated
    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];
      const row = { ...characterToRow({ ...char, sort_order: i + 1 }), updated_at: new Date().toISOString() };
      if (!existingIds.has(char.id)) {
        // New character — insert without id (let DB generate uuid)
        const { id: _id, ...rowWithoutId } = row as any;
        const { error: insertError } = await supabase.from("characters").insert({ ...rowWithoutId });
        if (insertError) {
          console.error("Insert error:", insertError);
          alert(`保存に失敗しました: ${insertError.message}`);
          setLoading(false);
          return;
        }
      } else {
        const { error: updateError } = await supabase.from("characters").update(row).eq("id", char.id);
        if (updateError) {
          console.error("Update error:", updateError);
          alert(`保存に失敗しました: ${updateError.message}`);
          setLoading(false);
          return;
        }
      }
    }

    await fetchCharacters();
  };

  // Optimistic toggle — update UI immediately, save to DB in background
  const handleToggleActiveOptimistic = (id: string) => {
    setCharacters((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isActive: !c.isActive } : c))
    );
    const target = characters.find((c) => c.id === id);
    if (!target) return;
    const newActive = !target.isActive;
    supabase
      .from("characters")
      .update({ is_active: newActive, updated_at: new Date().toISOString() })
      .eq("id", id)
      .then(({ error }) => {
        if (error) {
          // Revert on error
          setCharacters((prev) =>
            prev.map((c) => (c.id === id ? { ...c, isActive: target.isActive } : c))
          );
        }
      });
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-pink-400 text-3xl animate-spin"></i>
          <p className="text-pink-400 text-sm">データを読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminDashboard
      characters={characters}
      onSave={handleSave}
      onToggleActiveOptimistic={handleToggleActiveOptimistic}
      onLogout={handleLogout}
    />
  );
};

export default AdminPage;
